import { SingleSelect } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Typography
} from "@material-ui/core";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, { useEffect } from "react";
import { DevTool } from "@hookform/devtools";
import { ReferenceItemFragment } from "@graphql";
import moment from "moment";

type Props = {
  data: ReferenceItemFragment;
  references: ReferenceItemFragment[];
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit: (refId: string, mergeWithRefId: string) => void;
  loading?: boolean;
};

export const ReferenceMergeModal = ({
  data,
  open,
  setOpen,
  onSubmit,
  loading,
  references
}: Props) => {
  const classes = useStyles();
  const form = useForm<any>({
    defaultValues: {
      reference: null
    },
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const handleSubmit = () => {
    const mergeWithRefId = form.getValues("reference").value;

    onSubmit(data._id, mergeWithRefId);
  };

  useEffect(() => {
    if (open) {
      form.trigger();
    } else {
      form.reset();
    }
  }, [open]);

  const canSubmit = form.formState.isValid && !loading;

  return (
    <FormProvider {...form}>
      <Dialog open={open} maxWidth="md" fullWidth onClose={() => setOpen(false)}>
        <DevTool control={form.control} placement={"top-right"} />
        <DialogTitle>
          <Typography variant={"h3"}>
            {locale.Merge}{" "}
            {data.formType ? locale.consultantReferenceType[data.formType] : ""}
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant={"body1"}>{data.position}</Typography>
              <Typography variant={"body1"}>{data.workPlace}</Typography>
              <Typography variant={"body1"}>
                Senast redigerad: {moment(data.updatedOn).format("L")}
              </Typography>
              <Typography variant={"body1"}>
                {locale.consultantReferenceStatus[data.status]}
              </Typography>
              <Typography variant={"body1"}>
                {locale.consultantReferenceRelation[data.workRelation]}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="reference"
                control={form.control}
                render={({ field, fieldState }) => (
                  <SingleSelect
                    {...field}
                    error={!!fieldState.error}
                    inputLabel={locale.Reference}
                    menuPortalTarget={document.body}
                    styleProps={{
                      control: { showBorder: true },
                      menu: { showBorder: true },
                      menuPortal: { zIndex: 9999 }
                    }}
                    placeholder=""
                    isClearable
                    options={references.toLabelValue(
                      (x) => x.name,
                      (x) => x._id
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box className={classes.btnWrap}>
            <Button
              color={"primary"}
              className={classes.cancelBtn}
              onClick={() => setOpen(false)}
            >
              {locale.Cancel}
            </Button>

            <Button
              color="primary"
              variant={"contained"}
              disabled={!canSubmit}
              onClick={() => {
                handleSubmit();
              }}
            >
              Ok
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const schema = yup.object().shape({
  reference: yup.object().shape({
    value: yup.string().required()
  })
});

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    overflow: "hidden"
  },
  btnWrap: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  cancelBtn: {
    marginRight: "auto"
  }
}));
